import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { laneApi, LaneCounts, LaneId, LANE_IDS, LaneStatusCounts } from '../../api/laneApi';
import {
  LaneTab, filtersFromSearchParams, applyTab, applyPage, applySelectedTask,
} from './laneUrlState';
import { Button, Toast, ToastMessage } from '../../components/ui';
import { CreateTaskModal } from './CreateTaskModal';
import { ImportDialog } from './ImportDialog';
import { ExportDialog } from './ExportDialog';
import LenderEmailFilter from '../../components/LenderEmailFilter';
import { Omnibox } from './Omnibox';
import { AssignedFilterDropdown } from './AssignedFilterDropdown';
import { TaskTypeFilterDropdown } from './TaskTypeFilterDropdown';
import './lanes.css';
import './laneshell-controls.css';

const LANE_LABELS: Record<LaneId, string> = {
  client: 'Client',
  lender: 'Lender',
  post: 'Post',
  manual: 'Manual',
};

/** Tabs rendered in the shell's tab strip. The Post lane duplicate was
 * removed -- the dedicated `/post-tasks` page remains the postal workflow,
 * so `post` is deliberately excluded here even though `LANE_IDS` (and the
 * `/ui/lanes/counts` response consumed below) still carry it. */
const VISIBLE_LANE_TABS: LaneId[] = LANE_IDS.filter((id) => id !== 'post');

/** Pure formatter for the Pending/Completed sub-tab label -- Task 11 (John's
 * ask). `count` is `undefined` while the owning lane hasn't fetched a
 * response with `statusCounts` yet (initial load) or, for a stale/older
 * backend response shape, never will -- either way the parenthetical is
 * simply omitted rather than showing a stale or fabricated "(0)". A defined
 * count always renders through `toLocaleString()` so five- and six-figure
 * lanes read as "61,204" rather than "61204". Exported (rather than kept
 * file-private) so it has its own direct unit test, independent of the full
 * LaneShell render tree. */
export function formatStatusTabLabel(label: string, count: number | undefined): string {
  return count === undefined ? label : `${label} (${count.toLocaleString()})`;
}

/** Context handed to the lane-specific list slot. */
export interface LaneListSlotProps {
  tab: LaneTab;
  page: number;
  pageSize: number;
  selectedTaskId: string | null;
  onSelectTask: (taskId: string | null) => void;
  onPageChange: (page: number) => void;
  /** Bumps every time the shell's Refresh button (or a create) fires, so a
   * lane body can key off it to re-fetch without owning the button itself. */
  refreshToken: number;
  /** Surfaces a one-line success/error/info banner via the shell's shared
   * `Toast` (Task 12) -- reconnects the feedback CreateTaskModal's
   * extraction lost (T8 review flag) and gives bulk/row actions a single
   * place to report results without each lane owning its own banner markup. */
  showToast: (toast: ToastMessage) => void;
}

/** Context handed to the lane-specific detail slot. */
export interface LaneDetailSlotProps {
  selectedTaskId: string | null;
  refreshToken: number;
  /** Same shared toast trigger as `LaneListSlotProps.showToast` -- the detail
   * pane's assign/forward/complete/archive actions report through it too. */
  showToast: (toast: ToastMessage) => void;
}

export interface LaneShellProps {
  lane: LaneId;
  /** Left list-region slot -- Tasks 9-11 fill this in per lane. Renders a
   * "Lane content arrives in Task 9-11" placeholder when omitted so the
   * shell alone is demoable. */
  renderList?: (props: LaneListSlotProps) => React.ReactNode;
  /** Right detail-region slot -- same placeholder fallback. */
  renderDetail?: (props: LaneDetailSlotProps) => React.ReactNode;
  /** Slot for lane-specific filter chips (e.g. the lender lane's
   * since-01/07/2026 chip), rendered next to the omnibox. */
  renderChips?: (props: { refreshToken: number }) => React.ReactNode;
  /** Total row count for the active tab/filters, if the lane body knows it
   * (used only to render "Page N of M"; omit to show just "Page N"). */
  totalCount?: number;
  /** Pending/completed split for the active filters (Task 11) -- rendered
   * as the parenthetical on the Pending/Completed sub-tab buttons, e.g.
   * "Pending (61,204)". Optional end-to-end: `undefined` while loading (incl.
   * ClientLane's grouped-accordion path pre-fetch, which gets its own
   * statusCounts from GET /ui/lanes/client/groups as of Task 15), or against
   * a stale backend that predates this field -- any of those simply render
   * the bare "Pending"/"Completed" label instead of a stale or fabricated
   * count. */
  statusCounts?: LaneStatusCounts;
  /** Caps the list/detail split at viewport height so each pane scrolls
   * independently instead of growing the page, and resets both panes' scroll
   * to the top whenever a task is selected. Opt-in per lane: Client, Lender and
   * Manual all set it; Post does not, so it keeps the grow-with-content layout.
   * Applies to Completed as well as Pending — there is one split element and the
   * sub-tabs only swap which data the slots render. */
  viewportSplit?: boolean;
}

/**
 * Shared shell for the task lanes (client/lender/manual) -- Tasks Rebuild
 * Task 8. Owns: lane tabs with live count badges, the omnibox
 * slot (the typed-operator Omnibox, Task 13), the chip-bar slot, Pending/
 * Completed sub-tabs, the split-pane scaffold, pagination footer, and the
 * toolbar (Create Task / More menu / Refresh). Lane-specific list+detail
 * bodies are injected via render props from Tasks 9-11.
 */
export const LaneShell: React.FC<LaneShellProps> = ({
  lane, renderList, renderDetail, renderChips, totalCount, statusCounts, viewportSplit,
}) => {
  const { isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = filtersFromSearchParams(searchParams);

  // The capped, independently scrolling split is now standard wherever the lane
  // asks for it -- Client, Lender and Manual set `viewportSplit`, Post does not.
  // It used to be ANDed with a per-user `lanePinnedTasks` preference; that
  // preference and the pinned-open-task behaviour it also gated were removed
  // (Dec, 2026-08-27) in favour of one layout for everyone.
  const cappedSplit = Boolean(viewportSplit);

  const listPaneRef = useRef<HTMLDivElement>(null);
  const detailPaneRef = useRef<HTMLDivElement>(null);
  // Only the DETAIL pane resets on select: a newly opened email should start at
  // its top. The list pane deliberately stays where it is -- the row you just
  // clicked is somewhere down the list, and sending the list to the top would
  // throw away your place in it. (That reset existed because selecting used to
  // pin the row to row 1, so the top was where it had gone. Nothing moves now.)
  // Optional-call: scrollTo is absent in jsdom (tests).
  useEffect(() => {
    if (!cappedSplit || !filters.selected) return;
    detailPaneRef.current?.scrollTo?.({ top: 0 });
  }, [cappedSplit, filters.selected]);

  // The split's height is "viewport minus whatever sits above it", and that
  // differs per lane: Lender adds a chip bar, Manual a Task-type dropdown, and
  // both live in the wrapping toolbar so their height changes with viewport
  // width. Measuring the split's own offset keeps every lane correct instead of
  // needing a magic number each; the CSS falls back to the Client lane's 330px
  // until this runs. Guarded for jsdom, which has neither ResizeObserver nor
  // real layout.
  const splitRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!cappedSplit) return;
    const el = splitRef.current;
    if (!el) return;
    const measure = () => {
      const top = el.getBoundingClientRect().top + window.scrollY;
      // Leave a little breathing room below the panes rather than butting them
      // against the viewport edge.
      const next = `${Math.round(top + 24)}px`;
      // Only write a changed value: the offset drives the split's height, so
      // an unchanged write is wasted layout and a changed one must never
      // happen inside the observer's own callback (below).
      if (el.style.getPropertyValue('--lane-split-offset') !== next) {
        el.style.setProperty('--lane-split-offset', next);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    let ro: ResizeObserver | undefined;
    let raf = 0;
    if (typeof ResizeObserver !== 'undefined') {
      // Catches the toolbar re-wrapping and chips appearing/disappearing, which
      // a resize listener alone would miss. The measurement is deferred to the
      // next animation frame: writing the offset synchronously resizes the
      // split, and so the observed parent, inside the callback that was
      // reporting the parent's resize -- the browser flags that as
      // "ResizeObserver loop completed with undelivered notifications" (seen
      // on Dec's localhost while testing #656's email frame).
      ro = new ResizeObserver(() => {
        if (raf) return;
        raf = window.requestAnimationFrame(() => {
          raf = 0;
          measure();
        });
      });
      if (el.parentElement) ro.observe(el.parentElement);
    }
    return () => {
      window.removeEventListener('resize', measure);
      ro?.disconnect();
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [cappedSplit, lane, filters.tab]);

  const [counts, setCounts] = useState<LaneCounts | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [lenderRulesOpen, setLenderRulesOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const moreMenuRef = useRef<HTMLDivElement | null>(null);
  const [pageInput, setPageInput] = useState(String(filters.page));

  const showToast = useCallback((t: ToastMessage) => setToast(t), []);

  const loadCounts = useCallback(() => {
    laneApi.getLaneCounts().then(setCounts).catch(() => {
      // Non-fatal: badges just stay stale/blank until the next poll.
    });
  }, []);

  useEffect(() => {
    loadCounts();
    const timer = setInterval(loadCounts, 60000);
    return () => clearInterval(timer);
  }, [loadCounts]);

  useEffect(() => {
    if (!moreOpen) return;
    const onDown = (e: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target as Node)) setMoreOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMoreOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [moreOpen]);

  const setTab = (tab: LaneTab) => setSearchParams((prev) => applyTab(prev, tab));
  const setPage = (page: number) => setSearchParams((prev) => applyPage(prev, page));
  const setSelectedTaskId = (taskId: string | null) => setSearchParams((prev) => applySelectedTask(prev, taskId));
  const refresh = () => setRefreshToken((t) => t + 1);

  const totalPages = totalCount != null ? Math.max(1, Math.ceil(totalCount / filters.pageSize)) : null;

  // Keeps the go-to-page input in sync with the URL when the page changes
  // via Prev/Next, back/forward navigation, or a fresh deep link -- mirrors
  // Omnibox's identical `useEffect(() => setInputValue(filters.q...` idiom
  // for keeping a locally-buffered input aligned with URL-derived state.
  useEffect(() => { setPageInput(String(filters.page)); }, [filters.page]);

  /** Commits the go-to-page input on Enter/blur. Only a bare positive
   * integer is accepted -- anything else (empty, non-numeric, decimals)
   * reverts the input to the current page rather than being silently
   * parsed/truncated. A numeric value is clamped into [1, totalPages] when
   * totalPages is known (an unknown total, i.e. no lane has passed
   * `totalCount`, only floors at 1). Writes through the same `setPage` ->
   * `applyPage` URL param Prev/Next already use, so there is exactly one
   * source of truth for the page param. */
  const commitPageInput = () => {
    const trimmed = pageInput.trim();
    if (!/^\d+$/.test(trimmed)) {
      setPageInput(String(filters.page));
      return;
    }
    const parsed = parseInt(trimmed, 10);
    const clamped = totalPages != null ? Math.min(Math.max(parsed, 1), totalPages) : Math.max(parsed, 1);
    setPageInput(String(clamped));
    if (clamped !== filters.page) setPage(clamped);
  };

  const handlePageInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitPageInput();
    }
  };

  return (
    <div className="lane-shell">
      <div className="lane-shell__tabs" role="tablist" aria-label="Task lanes">
        {VISIBLE_LANE_TABS.map((id) => (
          <Link
            key={id}
            to={`/tasks/${id}`}
            role="tab"
            aria-selected={lane === id}
            className={`lane-shell__tab ${lane === id ? 'lane-shell__tab--active' : ''}`}
          >
            {LANE_LABELS[id]}
            {counts && <span className="lane-shell__tab-count">{counts[id]}</span>}
          </Link>
        ))}
        {isAdmin && (
          <Link to="/tasks/dashboard" role="tab" className="lane-shell__tab lane-shell__tab--dashboard">
            Dashboard
          </Link>
        )}
      </div>

      <div className="lane-shell__toolbar">
        <div className="lane-shell__omnibox">
          <Omnibox lane={lane} />
        </div>
        <AssignedFilterDropdown lane={lane} />
        {/* Manual lane only: the ring-fence/standard split is meaningless on
            the other lanes (their email_source values are inbox provenance,
            not a task type). John's ask, 17 Aug. */}
        {lane === 'manual' && <TaskTypeFilterDropdown />}
        {renderChips && <div className="lane-shell__chipbar">{renderChips({ refreshToken })}</div>}
        <div className="lane-shell__actions">
          <Button onClick={() => setCreateOpen(true)}>+ Create Task</Button>
          <div className="lane-shell__more" ref={moreMenuRef}>
            <Button
              variant="secondary"
              onClick={() => setMoreOpen((o) => !o)}
              aria-haspopup="menu"
              aria-expanded={moreOpen}
              title="More actions"
            >
              &#8942; More
            </Button>
            {moreOpen && (
              <div role="menu" className="lane-shell__more-menu">
                <button role="menuitem" onClick={() => { setMoreOpen(false); setImportOpen(true); }}>
                  &#8593; Import tasks
                </button>
                <button role="menuitem" onClick={() => { setMoreOpen(false); setExportOpen(true); }}>
                  &#8595; Export tasks
                </button>
                <button role="menuitem" onClick={() => { setMoreOpen(false); setLenderRulesOpen(true); }}>
                  Lender email rules…
                </button>
              </div>
            )}
          </div>
          <Button variant="ghost" onClick={refresh} title="Refresh">
            &#8635; Refresh
          </Button>
        </div>
      </div>

      <div className="lane-shell__subtabs" role="tablist" aria-label="Status">
        <button
          role="tab"
          aria-selected={filters.tab === 'pending'}
          onClick={() => setTab('pending')}
          className={`lane-shell__subtab ${filters.tab === 'pending' ? 'lane-shell__subtab--active' : ''}`}
        >
          {formatStatusTabLabel('Pending', statusCounts?.pending)}
        </button>
        <button
          role="tab"
          aria-selected={filters.tab === 'completed'}
          onClick={() => setTab('completed')}
          className={`lane-shell__subtab ${filters.tab === 'completed' ? 'lane-shell__subtab--active' : ''}`}
        >
          {formatStatusTabLabel('Completed', statusCounts?.completed)}
        </button>
      </div>

      <div
        ref={splitRef}
        className={`lane-shell__split${cappedSplit ? ' lane-shell__split--viewport' : ''}`}
      >
        <div className="lane-shell__list" ref={listPaneRef}>
          {renderList
            ? renderList({
                tab: filters.tab,
                page: filters.page,
                pageSize: filters.pageSize,
                selectedTaskId: filters.selected,
                onSelectTask: setSelectedTaskId,
                onPageChange: setPage,
                refreshToken,
                showToast,
              })
            : <div className="lane-shell__placeholder">Lane content arrives in Task 9-11</div>}
        </div>
        <div className="lane-shell__detail" ref={detailPaneRef}>
          {renderDetail
            ? renderDetail({ selectedTaskId: filters.selected, refreshToken, showToast })
            : <div className="lane-shell__placeholder">Lane content arrives in Task 9-11</div>}
        </div>
      </div>

      <div className="lane-shell__pagination">
        <button disabled={filters.page <= 1} onClick={() => setPage(filters.page - 1)}>&lsaquo; Prev</button>
        <span className="lane-shell__page-jump">
          Page{' '}
          <input
            type="text"
            inputMode="numeric"
            className="lane-shell__page-input"
            aria-label="Go to page"
            value={pageInput}
            onChange={(e) => setPageInput(e.target.value)}
            onKeyDown={handlePageInputKeyDown}
            onBlur={commitPageInput}
            // Disabled until the owning lane supplies a totalCount (totalPages
            // still null): without a known upper bound a committed jump can
            // only floor at 1, letting a user submit an out-of-range page
            // (e.g. 500 when there turn out to be 3) that round-trips to the
            // backend. Loose `== null` matches the `totalCount != null` idiom
            // used elsewhere in this file and also catches undefined.
            disabled={totalPages == null}
          />
          {totalPages != null ? ` of ${totalPages}` : ''}
        </span>
        <button disabled={totalPages != null && filters.page >= totalPages} onClick={() => setPage(filters.page + 1)}>
          Next &rsaquo;
        </button>
      </div>

      <CreateTaskModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => { refresh(); loadCounts(); showToast({ type: 'success', message: 'Task created.' }); }}
      />
      <ImportDialog
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => { refresh(); loadCounts(); }}
        showToast={showToast}
      />
      <ExportDialog isOpen={exportOpen} onClose={() => setExportOpen(false)} showToast={showToast} />
      <LenderEmailFilter isOpen={lenderRulesOpen} onClose={() => setLenderRulesOpen(false)} />
      <Toast toast={toast} onDismiss={() => setToast(null)} />
    </div>
  );
};

export default LaneShell;
